import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Chip,
  Box,
  TextField,
  Avatar,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  AttachFile as AttachFileIcon,
  GetApp as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
  Fullscreen as FullscreenIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@material-ui/icons";

// Utils
import {
  statusOptions,
  priorityOptions,
  getStatusColor,
  getPriorityColor,
  formatDate
} from "./utils";

import {
  getSupportTicket,
  postCommentSupport
} from "../../../services/support";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(95vh - 120px)", // Desconta altura do DialogTitle
    width: "100%",
    backgroundColor: "#f8f9fa",
    overflow: "hidden",
  },
  unifiedHeader: {
    display: "flex",
    height: "60px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e9ecef",
    flexShrink: 0,
  },
  leftHeaderSection: {
    width: "60%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 3),
    borderRight: "1px solid #e9ecef",
  },
  rightHeaderSection: {
    width: "40%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 3),
  },
  panelsContainer: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  leftPanel: {
    width: "60%",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRight: "1px solid #e9ecef",
    height: "100%",
  },
  rightPanel: {
    width: "40%",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1),
    backgroundColor: "#fff",
    flexShrink: 0,
    height: "48px",
    minHeight: "48px",
    borderBottom: "1px solid #e9ecef",
  },
  taskInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  taskType: {
    fontSize: "0.875rem",
    color: "#6c757d",
    fontWeight: 500,
  },
  taskId: {
    fontSize: "0.875rem",
    color: "#6c757d",
    fontFamily: "monospace",
  },
  aiButton: {
    backgroundColor: "#6366f1",
    color: "#fff",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#4f46e5",
    },
  },
  topBarActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  titleSection: {
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #e9ecef",
    flexShrink: 0,
    backgroundColor: "#fff",
  },
  title: {
    fontWeight: 700,
    color: "#1f2937",
    fontSize: "1.5rem",
    lineHeight: 1.3,
    marginBottom: theme.spacing(1),
  },
  aiSuggestionBar: {
    backgroundColor: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  aiSuggestionText: {
    fontSize: "0.875rem",
    color: "#6b7280",
    textAlign: "center",
  },
  leftContent: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(3),
    minHeight: 0, // Permite que o flex shrink funcione
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d1d5db",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "#9ca3af",
      },
    },
  },
  essentialFields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  fieldGroup: {
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: theme.spacing(2),
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(1),
  },
  fieldValue: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#1f2937",
  },
  statusButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#fff",
    minWidth: "120px",
    justifyContent: "space-between",
  },
  timeButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "0.875rem",
    color: "#6b7280",
    "&:hover": {
      backgroundColor: "#f9fafb",
    },
  },
  emptyValue: {
    color: "#9ca3af",
    fontStyle: "italic",
  },
  descriptionSection: {
    marginTop: theme.spacing(3),
  },
  descriptionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(1),
  },
  description: {
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: theme.spacing(2),
    minHeight: "120px",
    fontSize: "0.875rem",
    color: "#374151",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
  },
  attachmentsSection: {
    marginTop: theme.spacing(3),
  },
  attachmentsLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  attachmentsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  attachmentItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f3f4f6",
      borderColor: "#d1d5db",
    },
  },
  attachmentIcon: {
    color: "#6b7280",
    fontSize: "1.5rem",
    width: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    flexShrink: 0,
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "4px",
  },
  thumbnailPdf: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#dc2626",
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: "bold",
    textAlign: "center",
  },
  thumbnailFile: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    fontSize: "1.2rem",
  },
  thumbnailDoc: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2563eb",
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: "bold",
    textAlign: "center",
  },
  thumbnailExcel: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#059669",
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: "bold",
    textAlign: "center",
  },
  attachmentInfo: {
    flex: 1,
    minWidth: 0,
  },
  attachmentName: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#1f2937",
    wordBreak: "break-all",
  },
  attachmentSize: {
    fontSize: "0.75rem",
    color: "#6b7280",
    marginTop: "2px",
  },
  downloadButton: {
    color: "#6b7280",
    "&:hover": {
      backgroundColor: "#e5e7eb",
      color: "#374151",
    },
  },
  attachmentClickable: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f3f4f6",
      borderColor: "#d1d5db",
    },
  },
  previewModal: {
    "& .MuiDialog-paper": {
      maxWidth: "90vw",
      maxHeight: "90vh",
      margin: "20px",
    },
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  },
  previewTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#1f2937",
    flex: 1,
    marginRight: theme.spacing(2),
    wordBreak: "break-all",
  },
  previewActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  previewContent: {
    padding: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    backgroundColor: "#f8f9fa",
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  pdfPreview: {
    width: "100%",
    height: "70vh",
    border: "none",
    borderRadius: "8px",
  },
  filePreview: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  filePreviewIcon: {
    fontSize: "4rem",
    color: "#6b7280",
    marginBottom: theme.spacing(2),
  },
  filePreviewText: {
    fontSize: "1.1rem",
    color: "#374151",
    marginBottom: theme.spacing(1),
  },
  filePreviewSubtext: {
    fontSize: "0.9rem",
    color: "#6b7280",
    marginBottom: theme.spacing(3),
  },
  rightHeader: {
    padding: theme.spacing(1),
    borderBottom: "1px solid #e9ecef",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    backgroundColor: "#fff",
    height: "48px", // Mesma altura do topBar
    minHeight: "48px",
  },
  activityTitle: {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: "1rem",
  },
  activityList: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1, 2),
    minHeight: 0, // Permite que o flex shrink funcione
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d1d5db",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "#9ca3af",
      },
    },
  },
  activityItem: {
    display: "flex",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid #f3f4f6",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  activityItemRight: {
    display: "flex",
    flexDirection: "row-reverse",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid #f3f4f6",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  activityAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.875rem",
    flexShrink: 0,
  },
  activityContent: {
    flex: 1,
    minWidth: 0, // Permite text truncation
  },
  activityContentRight: {
    flex: 1,
    minWidth: 0,
    textAlign: "right",
  },
  activityAuthor: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#1f2937",
    marginBottom: theme.spacing(0.5),
  },
  activityText: {
    fontSize: "0.875rem",
    color: "#374151",
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
  messageLeft: {
    backgroundColor: "#f3f4f6",
    borderRadius: "18px 18px 18px 4px",
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    maxWidth: "100%",
    position: "relative",
  },
  messageRight: {
    backgroundColor: "#F97B18",
    color: "#fff",
    borderRadius: "18px 18px 4px 18px",
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    maxWidth: "100%",
    position: "relative",
    marginLeft: "auto",
  },
  messageText: {
    fontSize: "0.875rem",
    lineHeight: 1.5,
    wordWrap: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "pre-wrap",
    margin: 0,
  },
  messageTextRight: {
    fontSize: "0.875rem",
    lineHeight: 1.5,
    wordWrap: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "pre-wrap",
    margin: 0,
    color: "#fff",
  },
  activityTime: {
    fontSize: "0.75rem",
    color: "#6b7280",
  },
  activityTimeRight: {
    fontSize: "0.75rem",
    color: "#6b7280",
    textAlign: "right",
  },
  messageTime: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    marginTop: theme.spacing(0.5),
    textAlign: "right",
  },
  messageTimeRight: {
    fontSize: "0.7rem",
    color: "rgba(255, 255, 255, 0.8)",
    marginTop: theme.spacing(0.5),
    textAlign: "right",
  },
  commentImage: {
    maxWidth: "200px",
    maxHeight: "150px",
    objectFit: "cover",
    borderRadius: "8px",
    marginTop: theme.spacing(1),
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  commentImageContainer: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  commentImageContainerRight: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    alignItems: "flex-end",
  },
  activityActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  actionButton: {
    padding: theme.spacing(0.5),
    minWidth: "auto",
    color: "#6b7280",
    "&:hover": {
      backgroundColor: "#f3f4f6",
    },
  },
  commentInputSection: {
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  commentInputRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  commentAttachmentPreview: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  attachmentPreviewItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#f3f4f6",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  attachmentPreviewImage: {
    width: "40px",
    height: "40px",
    objectFit: "cover",
    borderRadius: "4px",
  },
  attachmentPreviewInfo: {
    flex: 1,
    minWidth: 0,
  },
  attachmentPreviewName: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#374151",
    wordBreak: "break-all",
  },
  attachmentPreviewSize: {
    fontSize: "0.7rem",
    color: "#6b7280",
  },
  removeAttachmentButton: {
    color: "#ef4444",
    padding: "2px",
    "&:hover": {
      backgroundColor: "#fee2e2",
    },
  },
  commentInputField: {
    flex: 1,
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  inputTabs: {
    display: "flex",
    marginBottom: theme.spacing(1),
  },
  tab: {
    padding: theme.spacing(0.5, 1),
    fontSize: "0.75rem",
    color: "#6b7280",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    "&.active": {
      color: "#1f2937",
      borderBottomColor: "#6366f1",
    },
  },
  commentInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f9fafb",
      "& fieldset": {
        borderColor: "#e5e7eb",
      },
      "&:hover fieldset": {
        borderColor: "#6366f1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6366f1",
      },
    },
  },
  inputActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actionIcon: {
    padding: theme.spacing(0.5),
    color: "#6b7280",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f3f4f6",
      borderRadius: "4px",
    },
  },
  sendButton: {
    backgroundColor: "#6366f1",
    color: "#fff",
    height: "42px",
    width: "42px",
    minWidth: "42px",
    flexShrink: 0,
    alignSelf: "flex-end",
    "&:hover": {
      backgroundColor: "#4f46e5",
    },
    "&:disabled": {
      backgroundColor: "#d1d5db",
      color: "#9ca3af",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
}));

// Função para obter o ícone do arquivo baseado na extensão ou mimetype
const getFileIcon = (attachment) => {
  if (!attachment) return <FileIcon />;

  const extension = attachment.extension || attachment.title?.toLowerCase().split('.').pop();
  const mimetype = attachment.mimetype || '';

  // Verificar por mimetype primeiro
  if (mimetype.startsWith('image/')) {
    return <ImageIcon />;
  } else if (mimetype === 'application/pdf') {
    return <PdfIcon />;
  }

  // Fallback para extensão
  switch (extension) {
    case 'pdf':
      return <PdfIcon />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return <ImageIcon />;
    case 'doc':
    case 'docx':
      return <FileIcon />;
    case 'xls':
    case 'xlsx':
      return <FileIcon />;
    default:
      return <FileIcon />;
  }
};

// Função para formatar o tamanho do arquivo
const formatFileSize = (bytes) => {
  if (!bytes) return 'Tamanho desconhecido';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';

  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(2);

  return `${size} ${sizes[i]}`;
};

// Função para verificar se o arquivo é visualizável
const isPreviewable = (attachment) => {
  if (!attachment) return false;

  const extension = attachment.extension || attachment.title?.toLowerCase().split('.').pop();
  const mimetype = attachment.mimetype || '';

  // Verificar por mimetype primeiro
  if (mimetype.startsWith('image/') || mimetype === 'application/pdf') {
    return true;
  }

  // Fallback para extensão
  const previewableExtensions = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', // Imagens
    'pdf', // PDF
  ];

  return previewableExtensions.includes(extension);
};

// Função para obter o tipo de preview
const getPreviewType = (attachment) => {
  if (!attachment) return 'file';

  const extension = attachment.extension || attachment.title?.toLowerCase().split('.').pop();
  const mimetype = attachment.mimetype || '';

  // Verificar por mimetype primeiro
  if (mimetype.startsWith('image/')) {
    return 'image';
  } else if (mimetype === 'application/pdf') {
    return 'pdf';
  }

  // Fallback para extensão
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
    return 'image';
  } else if (extension === 'pdf') {
    return 'pdf';
  }

  return 'file';
};

// Função para extrair imagens dos comentários
const extractCommentImages = (comment) => {
  if (!comment?.comment) return [];

  const images = comment.comment.filter(c => c?.type === 'image' && c?.image);
  return images.map(imageComment => ({
    id: imageComment.image.id,
    name: imageComment.image.name || imageComment.image.title,
    title: imageComment.image.title || imageComment.image.name,
    url: imageComment.image.url,
    thumbnail_small: imageComment.image.thumbnail_small,
    thumbnail_medium: imageComment.image.thumbnail_medium,
    thumbnail_large: imageComment.image.thumbnail_large,
    width: imageComment.attributes?.width,
    naturalWidth: imageComment.attributes?.['data-natural-width'],
    naturalHeight: imageComment.attributes?.['data-natural-height'],
  }));
};

// Função para renderizar texto com quebras de linha
const renderTextWithLineBreaks = (text) => {
  if (!text) return null;

  return text.split('\n').map((line, index, array) => (
    <span key={index}>
      {line}
      {index < array.length - 1 && <br />}
    </span>
  ));
};

// Componente para renderizar imagem do comentário
const CommentImage = ({ image, classes, onClick }) => {
  const [imageError, setImageError] = useState(false);

  if (imageError) return null;

  return (
    <img
      src={image.thumbnail_medium || image.thumbnail_small || image.url}
      alt={image.title || image.name}
      className={classes.commentImage}
      onError={() => setImageError(true)}
      onClick={() => onClick && onClick(image)}
      loading="lazy"
    />
  );
};

// Componente de Thumbnail otimizado para ClickUp
const FileThumbnail = ({ attachment, classes, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const extension = attachment.extension || attachment.title?.toLowerCase()?.split('.').pop() || '';
  const mimetype = attachment.mimetype || '';

  // Para imagens - usar thumbnail do ClickUp se disponível
  if ((mimetype.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) && !imageError) {
    // Priorizar thumbnail_small, depois thumbnail_medium, depois url
    const imageSrc = attachment.thumbnail_small || attachment.thumbnail_medium || attachment.url;

    if (imageSrc) {
      return (
        <img
          src={imageSrc}
          alt={attachment.title || attachment.name || 'Anexo'}
          className={classes.thumbnailImage}
          onError={() => {
            console.log('Erro ao carregar thumbnail:', attachment);
            setImageError(true);
          }}
          onClick={onClick}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
        />
      );
    }
  }

  // Para PDFs
  if (mimetype === 'application/pdf' || extension === 'pdf') {
    return (
      <div
        className={classes.thumbnailPdf}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        PDF
      </div>
    );
  }

  // Para documentos Word
  if (['doc', 'docx'].includes(extension) || mimetype.includes('word')) {
    return (
      <div
        className={classes.thumbnailDoc}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        DOC
      </div>
    );
  }

  // Para planilhas Excel
  if (['xls', 'xlsx'].includes(extension) || mimetype.includes('sheet') || mimetype.includes('excel')) {
    return (
      <div
        className={classes.thumbnailExcel}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        XLS
      </div>
    );
  }

  // Para outros arquivos ou fallback
  return (
    <div
      className={classes.thumbnailFile}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {getFileIcon(attachment)}
    </div>
  );
};

const TicketDetails = ({
  ticket,
  onClose,
  onRefresh
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [updatedTicket, setUpdatedTicket] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    attachment: null,
    type: 'file'
  });
  const [activeTab, setActiveTab] = useState("comment");
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const activityListRef = useRef(null);
  const [validationModalOpen, setValidationModalOpen] = useState(false);

  // Buscar dados atualizados quando o componente montar
  useEffect(() => {
    if (ticket?.id) {
      fetchTicketData();
    }
  }, [ticket?.id]);

  // Fazer scroll para o final quando os comentários mudarem
  useEffect(() => {
    if (comments.length > 0) {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [comments.length]);

  // Abrir modal de validação quando status for "validação usuário"
  useEffect(() => {
    const displayTicket = updatedTicket || ticket;
    const statusLower = displayTicket?.status?.toLowerCase() || '';
    if (statusLower.includes('validação') && displayTicket.statusType !== 'closed') {
      setValidationModalOpen(true);
    }
  }, [updatedTicket, ticket]);

  const fetchTicketData = async () => {
    setLoading(true);
    try {
      const response = await getSupportTicket(ticket.id);

      const updatedTicket = mapTaskData(response.task);
      const sortedComments = mapComments(response.comments?.comments || []);

      setUpdatedTicket(updatedTicket);
      setComments(sortedComments);

      console.log("Dados atualizados do ticket:", updatedTicket);
      console.log("Comentários carregados:", sortedComments.length);

      // Scroll para o final após carregar comentários (primeira vez)
      if (sortedComments.length > 0) {
        setTimeout(() => {
          scrollToBottom();
        }, 200);
      }

    } catch (error) {
      console.error("Erro ao buscar dados do ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para extrair valores dos campos customizados
  const getCustomFieldValue = (customFields, fieldName) => {
    const field = customFields?.find(field => field.name === fieldName);
    return field?.value || null;
  };

  // Função para mapear dados da task
  const mapTaskData = (taskData) => ({
    id: taskData.id,
    title: taskData.name,
    description: taskData.text_content || taskData.description,
    status: taskData.status?.status || "unknown",
    statusType: taskData.status?.type || "unknown",
    priority: taskData.priority?.priority || "normal",
    createdAt: parseInt(taskData.date_created),
    createdAtFormatted: formatDate(parseInt(taskData.date_created)),
    updatedAt: parseInt(taskData.date_updated),
    attachments: taskData.attachments || [],
    creator: {
      name: taskData.creator?.username,
      email: taskData.creator?.email,
      profilePicture: taskData.creator?.profilePicture,
    },
    // Mapear campos customizados
    userName: getCustomFieldValue(taskData.custom_fields, "👤 Nome Usuário"),
    email: getCustomFieldValue(taskData.custom_fields, "✉️ E-mail de Contato"),
    storeId: getCustomFieldValue(taskData.custom_fields, "🆔 StoreID"),
    storeName: getCustomFieldValue(taskData.custom_fields, "🕋 Loja"),
    cnpj: getCustomFieldValue(taskData.custom_fields, "🕋 CNPJ"),
    category: getCustomFieldValue(taskData.custom_fields, "Categoria"),
    classification: getCustomFieldValue(taskData.custom_fields, "Classificação"),
    solutionApplied: getCustomFieldValue(taskData.custom_fields, "✅ Solução Aplicada"),
    rootCause: getCustomFieldValue(taskData.custom_fields, "🐞 Causa Raiz"),
    observations: getCustomFieldValue(taskData.custom_fields, "💬 Observações"),
    nps: getCustomFieldValue(taskData.custom_fields, "NPS"),
    rating: getCustomFieldValue(taskData.custom_fields, "⭐ Avaliação"),
    url: taskData.url,
  });

  // Função para mapear comentários
  const mapComments = (apiComments) => {
    const mappedComments = apiComments.map(comment => ({
      id: comment.id,
      content: comment.comment_text || '',
      author: comment.user?.id !== 106033745 ? comment.user?.username : 'Você',
      createdAt: parseInt(comment.date),
      createdAtFormatted: formatDate(parseInt(comment.date)),
      userAvatar: comment.user?.profilePicture,
      userInitials: comment.user?.initials,
      images: extractCommentImages(comment), // Extrair todas as imagens
      attachments: comment.attachments || [], // Anexos enviados pelo usuário
    }));

    return mappedComments.sort((a, b) => a.createdAt - b.createdAt)//.filter(comment => comment?.author !== "ClickBot");
  };

  const handleSendComment = async () => {
    if (!newComment.trim() && commentAttachments.length === 0) return;

    const displayTicket = updatedTicket || ticket;
    // Verificar se o chamado está encerrado
    if (displayTicket.statusType === 'closed') {
      alert('Este chamado foi encerrado e não é possível adicionar novos comentários.');
      return;
    }

    try {
      setLoading(true);
      console.log("Enviando comentário:", newComment, "com anexos:", commentAttachments);

      // Extrair apenas os arquivos File para enviar via FormData
      const fileAttachments = commentAttachments.map(attachment => {
        console.log('Debug - processando attachment:', attachment);
        console.log('Debug - attachment.file:', attachment.file);
        console.log('Debug - é File?', attachment.file instanceof File);
        return attachment.file;
      });

      console.log('Debug - fileAttachments final:', fileAttachments);

      const newCommentObj = {
        id: Date.now(),
        content: newComment,
        author: "Você",
        createdAt: new Date().toISOString(),
        createdAtFormatted: "Agora",
        attachments: fileAttachments, // Enviar os File objects
      };

      await postCommentSupport(ticket.id, newCommentObj);
      setNewComment("");
      setCommentAttachments([]);

      // Scroll imediato para dar feedback visual
      setTimeout(() => {
        scrollToBottom();
      }, 50);

    } catch (error) {
      console.error("Erro ao enviar comentário:", error);
    }
    finally {
      fetchTicketData();
      setLoading(false);
    }
  };

  const handleCommentFileUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert(`Arquivo ${file.name} é muito grande. Limite de 10MB.`);
        return;
      }

      console.log('Debug - arquivo selecionado:', file);

      const baseAttachment = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file, // Preservar o File object original
        isImage: file.type.startsWith('image/')
      };

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const attachment = {
            ...baseAttachment,
            preview: e.target.result
          };
          console.log('Debug - attachment criado (imagem):', attachment);
          setCommentAttachments(prev => [...prev, attachment]);
        };
        reader.readAsDataURL(file);
      } else {
        // Para arquivos não-imagem, adicionar diretamente
        console.log('Debug - attachment criado (não-imagem):', baseAttachment);
        setCommentAttachments(prev => [...prev, baseAttachment]);
      }
    });

    // Limpar input
    event.target.value = '';
  };

  const handleRemoveCommentAttachment = (attachmentId) => {
    setCommentAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  // Função para fazer scroll para o final da lista
  const scrollToBottom = () => {
    if (activityListRef.current) {
      const scrollElement = activityListRef.current;
      scrollElement.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleDownloadAttachment = (attachment) => {
    try {
      // Para anexos do ClickUp, usar url_w_query se disponível, senão url
      const downloadUrl = attachment.url_w_query || attachment.url;

      if (downloadUrl) {
        // Criar link de download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = attachment.title || attachment.name || 'anexo';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.warn('Anexo sem URL de download:', attachment);
      }
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
    }
  };

  const handlePreviewAttachment = (attachment) => {
    console.log('Abrindo preview para anexo:', attachment);
    const previewType = getPreviewType(attachment);
    console.log('Tipo de preview determinado:', previewType);
    setPreviewModal({
      open: true,
      attachment: attachment,
      type: previewType
    });
  };

  const handlePreviewCommentImage = (image) => {
    console.log('Abrindo preview para imagem do comentário:', image);
    // Converter formato da imagem do comentário para o formato esperado pelo modal
    const attachmentFormat = {
      id: image.id,
      title: image.title || image.name,
      name: image.name || image.title,
      url: image.url,
      thumbnail_small: image.thumbnail_small,
      thumbnail_medium: image.thumbnail_medium,
      thumbnail_large: image.thumbnail_large,
      mimetype: 'image/png', // Assumir PNG por padrão
      extension: 'png'
    };

    setPreviewModal({
      open: true,
      attachment: attachmentFormat,
      type: 'image'
    });
  };

  const handleClosePreview = () => {
    setPreviewModal({
      open: false,
      attachment: null,
      type: 'file'
    });
  };

  if (!ticket) {
    return (
      <Box textAlign="center" padding={3}>
        <Typography variant="h6" color="textSecondary">
          Nenhum ticket selecionado
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const displayTicket = updatedTicket || ticket;

  return (
    <div className={classes.root}>
      {/* Header Superior Unificado */}
      <div className={classes.unifiedHeader}>
        <div className={classes.leftHeaderSection}>
          <div className={classes.taskInfo}>
            <Typography className={classes.taskType}>ID: </Typography>
            <Typography className={classes.taskId}>{displayTicket.id}</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>
                Status:
              </Typography>
              <Chip
                label={displayTicket.status?.toUpperCase().replace(/_/g, ' ') || 'DESCONHECIDO'}
                style={{
                  backgroundColor: displayTicket.statusType === 'closed' ? '#dc2626' : '#059669',
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: "24px",
                  borderRadius: "12px"
                }}
                size="small"
              />
            </div>
            <IconButton
              onClick={fetchTicketData}
              size="small"
              style={{ color: "#6b7280" }}
            >
              <RefreshIcon />
            </IconButton>
          </div>
        </div>
        <div className={classes.rightHeaderSection}>
          {/* Tags Minimalistas - Categoria e Classificação */}
          <Box display="flex" gap={2} alignItems="center" style={{ marginRight: '16px' }}>
            {displayTicket.classification && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.75rem',
                  color: '#8e9aaf',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                  lineHeight: '16px',
                  height: '16px'
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: displayTicket.classification.color || '#8e9aaf',
                    display: 'block'
                  }}
                />
                {displayTicket.classification.name || displayTicket.classification}
              </span>
            )}

            {displayTicket.category && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.75rem',
                  color: '#8e9aaf',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                  lineHeight: '16px',
                  height: '16px'
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: displayTicket.category.color || '#8e9aaf',
                    display: 'block'
                  }}
                />
                {displayTicket.category.name || displayTicket.category}
              </span>
            )}
          </Box>

          <Typography className={classes.activityTitle}>
            Atividade
          </Typography>
        </div>
      </div>

      {/* Container dos Painéis */}
      <div className={classes.panelsContainer}>
        {/* Painel Esquerdo - Detalhes do Ticket */}
        <div className={classes.leftPanel}>
          {/* Título */}
          <div className={classes.titleSection}>
            <Typography className={classes.title}>
              {displayTicket.title}
            </Typography>
          </div>

          {/* Conteúdo com Scroll */}
          <div className={classes.leftContent}>
            {/* Alerta de chamado encerrado */}
            {displayTicket.statusType === 'closed' && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                borderLeft: '4px solid #28a745'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#28a745',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '16px'
                  }}>
                    ✓
                  </div>
                  <Typography style={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}>
                    Chamado Resolvido
                  </Typography>
                </div>

                {(displayTicket.rootCause || displayTicket.solutionApplied) && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: displayTicket.rootCause && displayTicket.solutionApplied ? '1fr 1fr' : '1fr',
                    gap: '16px',
                    marginTop: '16px'
                  }}>
                    {displayTicket.rootCause && (
                      <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <Typography style={{
                          color: '#6c757d',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '8px'
                        }}>
                          Causa Raiz
                        </Typography>
                        <Typography style={{
                          color: '#495057',
                          fontSize: '0.875rem',
                          lineHeight: 1.5
                        }}>
                          {displayTicket.rootCause}
                        </Typography>
                      </div>
                    )}

                    {displayTicket.solutionApplied && (
                      <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <Typography style={{
                          color: '#6c757d',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '8px'
                        }}>
                          Solução Aplicada
                        </Typography>
                        <Typography style={{
                          color: '#495057',
                          fontSize: '0.875rem',
                          lineHeight: 1.5
                        }}>
                          {displayTicket.solutionApplied}
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Descrição */}
            <div className={classes.descriptionSection}>
              <div className={classes.descriptionLabel}>
                Descrição
              </div>
              <div className={classes.description}>
                {displayTicket.description || "Nenhuma descrição fornecida"}
              </div>
            </div>

            {/* Anexos */}
            {displayTicket.attachments && displayTicket.attachments.length > 0 && (
              <div className={classes.attachmentsSection}>
                <div className={classes.attachmentsLabel}>
                  <AttachFileIcon style={{ fontSize: "1rem" }} />
                  Anexos ({displayTicket.attachments.length})
                </div>
                {/* Debug dos anexos */}
                {console.log('Anexos encontrados:', displayTicket.attachments)}
                <div className={classes.attachmentsList}>
                  {displayTicket.attachments.map((attachment, index) => (
                    <div
                      key={attachment.id || index}
                      className={`${classes.attachmentItem} ${isPreviewable(attachment) ? classes.attachmentClickable : ''}`}
                    >
                      <div className={classes.attachmentIcon}>
                        <FileThumbnail
                          attachment={attachment}
                          classes={classes}
                          onClick={isPreviewable(attachment) ? () => handlePreviewAttachment(attachment) : null}
                        />
                      </div>
                      <div
                        className={classes.attachmentInfo}
                        onClick={() => isPreviewable(attachment) && handlePreviewAttachment(attachment)}
                        style={{ cursor: isPreviewable(attachment) ? 'pointer' : 'default' }}
                      >
                        <div className={classes.attachmentName}>
                          {attachment.title || attachment.name || `Anexo ${index + 1}`}
                          {isPreviewable(attachment) && (
                            <ViewIcon
                              style={{
                                fontSize: "1rem",
                                marginLeft: "8px",
                                color: "#6b7280",
                                verticalAlign: "middle"
                              }}
                            />
                          )}
                        </div>
                        <div className={classes.attachmentSize}>
                          {formatFileSize(attachment.size)}
                          {isPreviewable(attachment) && (
                            <span style={{ color: "#059669", marginLeft: "8px", fontSize: "0.7rem" }}>
                              • Clique para visualizar
                            </span>
                          )}
                        </div>
                      </div>
                      <IconButton
                        className={classes.downloadButton}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadAttachment(attachment);
                        }}
                        title="Baixar anexo"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Painel Direito - Atividade/Comentários */}
        <div className={classes.rightPanel}>
          {/* Lista de Atividades/Comentários */}
          <div className={classes.activityList} ref={activityListRef}>
            {comments.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center',
                height: '100%'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  fontSize: '24px',
                  color: '#6c757d'
                }}>
                  💬
                </div>
                <Typography style={{
                  color: '#495057',
                  fontWeight: 600,
                  fontSize: '1rem',
                  marginBottom: '8px'
                }}>
                  Nenhuma atividade ainda
                </Typography>
                <Typography style={{
                  color: '#6c757d',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  maxWidth: '280px'
                }}>
                  Seja o primeiro a comentar neste chamado. Sua mensagem aparecerá aqui.
                </Typography>
              </div>
            ) : (
              comments.map((comment) => {
                const isCurrentUser = comment.author === 'Você';

                return (
                  <div
                    key={comment.id}
                    className={isCurrentUser ? classes.activityItemRight : classes.activityItem}
                  >
                    <Avatar
                      className={classes.activityAvatar}
                      src={comment.userAvatar}
                    >
                      {comment.userInitials || comment.author.charAt(0).toUpperCase()}
                    </Avatar>
                    <div className={isCurrentUser ? classes.activityContentRight : classes.activityContent}>

                      <div className={classes.activityAuthor}>
                        {comment.author}
                      </div>


                      {/* Mensagem estilo WhatsApp */}
                      <div className={isCurrentUser ? classes.messageRight : classes.messageLeft}>
                        <div className={isCurrentUser ? classes.messageTextRight : classes.messageText}>
                          {renderTextWithLineBreaks(comment.content)}
                        </div>

                        {/* Imagens do comentário */}
                        {comment.images && comment.images.length > 0 && (
                          <div className={isCurrentUser ? classes.commentImageContainerRight : classes.commentImageContainer}>
                            {comment.images.map((image, index) => (
                              <CommentImage
                                key={image.id || index}
                                image={image}
                                classes={classes}
                                onClick={handlePreviewCommentImage}
                              />
                            ))}
                          </div>
                        )}

                        {/* Anexos do comentário */}
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div className={isCurrentUser ? classes.commentImageContainerRight : classes.commentImageContainer}>
                            {comment.attachments.map((attachment, index) => (
                              <div key={attachment.id || index} className={classes.attachmentPreviewItem} style={{ marginTop: '4px' }}>
                                {attachment.isImage && attachment.preview ? (
                                  <img
                                    src={attachment.preview}
                                    alt={attachment.name}
                                    className={classes.attachmentPreviewImage}
                                    onClick={() => handlePreviewCommentImage({
                                      id: attachment.id,
                                      title: attachment.name,
                                      name: attachment.name,
                                      url: attachment.preview,
                                      thumbnail_small: attachment.preview,
                                      thumbnail_medium: attachment.preview,
                                      thumbnail_large: attachment.preview,
                                    })}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ) : (
                                  <div className={classes.attachmentPreviewImage} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    {getFileIcon({ extension: attachment.name?.split('.').pop(), mimetype: attachment.type })}
                                  </div>
                                )}
                                <div className={classes.attachmentPreviewInfo}>
                                  <div className={classes.attachmentPreviewName}>
                                    {attachment.name}
                                  </div>
                                  <div className={classes.attachmentPreviewSize}>
                                    {formatFileSize(attachment.size)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className={isCurrentUser ? classes.messageTimeRight : classes.messageTime}>
                          {comment.createdAtFormatted}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input de Comentário Fixo */}
          <div className={classes.commentInputSection}>
            {displayTicket.statusType === 'closed' ? (
              // Aviso para chamado encerrado
              <div style={{
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <Typography style={{
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}>
                  🔒 Chamado encerrado - Não é possível adicionar novos comentários
                </Typography>
              </div>
            ) : (
              <>
                {/* Preview dos anexos */}
                {commentAttachments.length > 0 && (
                  <div className={classes.commentAttachmentPreview}>
                    {commentAttachments.map((attachment) => (
                      <div key={attachment.id} className={classes.attachmentPreviewItem}>
                        {attachment.isImage ? (
                          <img
                            src={attachment.preview}
                            alt={attachment.name}
                            className={classes.attachmentPreviewImage}
                          />
                        ) : (
                          <div className={classes.attachmentPreviewImage} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #e5e7eb'
                          }}>
                            {getFileIcon({ extension: attachment.name.split('.').pop(), mimetype: attachment.type })}
                          </div>
                        )}
                        <div className={classes.attachmentPreviewInfo}>
                          <div className={classes.attachmentPreviewName}>
                            {attachment.name}
                          </div>
                          <div className={classes.attachmentPreviewSize}>
                            {formatFileSize(attachment.size)}
                          </div>
                        </div>
                        <IconButton
                          size="small"
                          className={classes.removeAttachmentButton}
                          onClick={() => handleRemoveCommentAttachment(attachment.id)}
                          title="Remover anexo"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
                {!displayTicket?.status?.toLowerCase()?.includes('validação') && (
                <div className={classes.commentInputRow}>
                  <TextField
                    multiline
                    placeholder="Insira seu comentário aqui..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    variant="outlined"
                    size="small"
                    className={classes.commentInput}
                    style={{ flex: 1 }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendComment();
                      }
                    }}
                  />

                  {/* Input de arquivo oculto */}
                  <input
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    style={{ display: 'none' }}
                    id="comment-file-upload"
                    multiple
                    type="file"
                    onChange={handleCommentFileUpload}
                  />

                  {/* Botão de anexar */}
                  <label htmlFor="comment-file-upload">
                    <IconButton
                      component="span"
                      size="small"
                      title="Anexar arquivo"
                      style={{ color: "#6b7280" }}
                    >
                      <AttachFileIcon />
                    </IconButton>
                  </label>

                  <IconButton
                    onClick={handleSendComment}
                    disabled={!newComment.trim() && commentAttachments.length === 0}
                    className={classes.sendButton}
                    size="small"
                  >
                    <SendIcon />
                  </IconButton>
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Visualização de Anexos */}
      <Dialog
        open={previewModal.open}
        onClose={handleClosePreview}
        maxWidth={false}
        className={classes.previewModal}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        <div style={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
          <div className={classes.previewHeader}>
            <Typography className={classes.previewTitle}>
              {previewModal.attachment?.title || previewModal.attachment?.name || 'Anexo'}
            </Typography>
            <div className={classes.previewActions}>
              <IconButton
                size="small"
                onClick={() => handleDownloadAttachment(previewModal.attachment)}
                title="Baixar arquivo"
                style={{ color: "#6b7280" }}
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClosePreview}
                title="Fechar"
                style={{ color: "#6b7280" }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <DialogContent className={classes.previewContent}>
            {previewModal.type === 'image' && previewModal.attachment && (
              <img
                src={previewModal.attachment.thumbnail_large || previewModal.attachment.thumbnail_medium || previewModal.attachment.url}
                alt={previewModal.attachment.title || previewModal.attachment.name}
                className={classes.imagePreview}
                onError={(e) => {
                  console.error('Erro ao carregar imagem:', e);
                  e.target.style.display = 'none';
                }}
              />
            )}

            {previewModal.type === 'pdf' && previewModal.attachment && (
              <iframe
                src={previewModal.attachment.url}
                className={classes.pdfPreview}
                title={previewModal.attachment.title || previewModal.attachment.name}
              />
            )}

            {(previewModal.type === 'file' || previewModal.type === 'text') && previewModal.attachment && (
              <div className={classes.filePreview}>
                <div className={classes.filePreviewIcon}>
                  {getFileIcon(previewModal.attachment)}
                </div>
                <Typography className={classes.filePreviewText}>
                  {previewModal.attachment.title || previewModal.attachment.name}
                </Typography>
                <Typography className={classes.filePreviewSubtext}>
                  {formatFileSize(previewModal.attachment.size)}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadAttachment(previewModal.attachment)}
                  style={{
                    backgroundColor: "#F97B18",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#D05D02" }
                  }}
                >
                  Baixar Arquivo
                </Button>
              </div>
            )}
          </DialogContent>
        </div>
      </Dialog>

      {/* Modal de Validação do Usuário */}
      <Dialog
        open={validationModalOpen}
        onClose={() => {
          setValidationModalOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ 
          backgroundColor: "#f8f9fa", 
          borderBottom: "1px solid #e9ecef",
          padding: "20px 24px"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#6366f1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "20px",
                flexShrink: 0
              }}>
                ✓
              </div>
              <Typography style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#1f2937"
              }}>
                Validação do Chamado
              </Typography>
            </div>
            <IconButton
              onClick={() => {
                setValidationModalOpen(false);
              }}
              style={{
                padding: "8px",
                color: "#6b7280"
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "24px" }}>
          <Typography style={{
            fontSize: "0.95rem",
            color: "#374151",
            lineHeight: 1.6,
            marginBottom: "20px"
          }}>
            O suporte aguarda sua validação. Por favor, informe se o problema foi resolvido:
          </Typography>
          
          <div style={{
            backgroundColor: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "20px"
          }}>
            <Typography style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#1f2937",
              marginBottom: "8px"
            }}>
              📌 O que fazer:
            </Typography>
            <ul style={{
              margin: 0,
              paddingLeft: "20px",
              fontSize: "0.875rem",
              color: "#4b5563",
              lineHeight: 1.8
            }}>
              <li>Clique em <strong>"Resolvido"</strong> se o problema foi solucionado - o chamado será fechado</li>
              <br /><li>Clique em <strong>"Não Resolvido"</strong> se ainda precisa de ajuda - o chamado voltará para "Em Progresso"</li>
              <br />
              <li>Você pode adicionar um comentário opcional explicando sua decisão</li>
            </ul>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setValidationModalOpen(false);
            }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TicketDetails;
